import {
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  type RefObject,
} from 'react';
import { cn } from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import { useIntersectionObserver } from 'usehooks-ts';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { checkIsNotFoundError } from '@/shared/utils/error-handler';
import { IDPDocumentSkeleton } from '@/modules/idp/components/IDPDocumentSkeleton';
import { getDocumentFileUrls } from '../../utils/idp-document-location';
import type { IIDPField } from '@/modules/idp/types';
import { transformToClientFields } from '@/modules/idp/utils/field-transformer';
import {
  EConfidenceLevel,
  getConfidenceLevel,
} from '@/modules/idp/utils/confidence';
import {
  ERenderMode,
  type TResolvedFile,
  type TResolvedValue,
} from '../../types';
import {
  getIDPBundleDocumentName,
  getIDPDocumentName,
  getIDPFieldsFromOutput,
  getIDPItemFields,
  isIDPOutput,
} from '../../utils';
import {
  DocumentPageSheet,
  FileDocumentPreview,
  MiniaturePage,
} from '../output-preview-primitives';

// Lazy: it pulls in pdf.js.
const FileRenderer = lazy(() =>
  import('./FileRenderer').then(m => ({ default: m.FileRenderer })),
);

/** One extracted field's box on the first page, as fractions of the page. */
interface IThumbnailBox {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
  /** Null when the extractor reported no confidence */
  level: EConfidenceLevel | null;
}

// Outlines only: fills hid the text and stacked into solid blocks.
const BOX_CLASS_BY_LEVEL: Record<EConfidenceLevel, string> = {
  [EConfidenceLevel.HIGH]: 'border-success',
  [EConfidenceLevel.MEDIUM]: 'border-warning',
  [EConfidenceLevel.LOW]: 'border-destructive',
};
const NO_CONFIDENCE_BOX_CLASS = 'border-muted-foreground/60';
const LEVEL_RANK: Record<EConfidenceLevel, number> = {
  [EConfidenceLevel.LOW]: 0,
  [EConfidenceLevel.MEDIUM]: 1,
  [EConfidenceLevel.HIGH]: 2,
};

/**
 * The less certain of two levels, so merging boxes never hides a low score.
 *
 * @param a - One level, or null for no confidence
 * @param b - The other
 * @returns The lower level; a reported level wins over none
 */
function lowerLevel(
  a: EConfidenceLevel | null,
  b: EConfidenceLevel | null,
): EConfidenceLevel | null {
  if (a === null) return b;
  if (b === null) return a;
  return LEVEL_RANK[a] <= LEVEL_RANK[b] ? a : b;
}

/**
 * The first-page boxes of the fields the IDP panel would draw, one per spot.
 * Pixel-space boxes are skipped; placing them needs the native page size.
 *
 * @param fields - The extraction's fields
 * @returns One box per located field on page 1
 */
export function getThumbnailBoxes(fields: IIDPField[]): IThumbnailBox[] {
  // Fields that share a box (one table's cells) draw once.
  const bySpot = new Map<string, IThumbnailBox>();
  for (const field of transformToClientFields(fields)) {
    const location = field.location;
    if (!location || location.pixel_space || location.page_index !== 0) {
      continue;
    }
    const box: IThumbnailBox = {
      id: field.id,
      left: location.left,
      top: location.top,
      width: location.right - location.left,
      height: location.bottom - location.top,
      level:
        field.confidence === undefined
          ? null
          : getConfidenceLevel(field.confidence),
    };
    const spot = [box.left, box.top, box.width, box.height]
      .map(n => n.toFixed(4))
      .join(':');
    const existing = bySpot.get(spot);
    bySpot.set(
      spot,
      existing
        ? { ...existing, level: lowerLevel(existing.level, box.level) }
        : box,
    );
  }
  return [...bySpot.values()];
}

interface IPageRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where the rendered page (canvas or image) sits in the sheet holding `ref`.
 *
 * @param ref - An element inside the sheet
 * @returns The page's box relative to the sheet, once drawn
 */
function useRenderedPageRect(ref: RefObject<HTMLElement | null>) {
  const [rect, setRect] = useState<IPageRect | null>(null);

  // eslint-disable-next-line no-restricted-syntax -- reason: pdf.js draws the page after mount, so its box can only be read from the DOM.
  useEffect(() => {
    const sheet = ref.current?.parentElement;
    if (!sheet || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const page = sheet.querySelector('canvas, img');
      // No page (new document, failed load): drop the position so the placeholder returns.
      if (!page) {
        setRect(null);
        return;
      }
      const sheetBox = sheet.getBoundingClientRect();
      const pageBox = page.getBoundingClientRect();
      if (!pageBox.width || !pageBox.height || !sheet.offsetWidth) return;
      // Divide out ancestor scale (the miniature, a dialog zoom); the overlay uses sheet pixels.
      const scale = sheetBox.width / sheet.offsetWidth;
      const next = {
        left: (pageBox.left - sheetBox.left) / scale,
        top: (pageBox.top - sheetBox.top) / scale,
        width: pageBox.width / scale,
        height: pageBox.height / scale,
      };
      // Keep the same object when nothing moved, or each react-pdf layer re-renders the table.
      setRect(previous =>
        previous &&
        previous.left === next.left &&
        previous.top === next.top &&
        previous.width === next.width &&
        previous.height === next.height
          ? previous
          : next,
      );
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(sheet);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(sheet, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['width', 'height', 'src'],
    });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [ref]);

  return rect;
}

/** Field boxes drawn over the page, at the page's position in the sheet. */
function FieldBoxes({
  boxes,
  page,
}: {
  boxes: IThumbnailBox[];
  page: IPageRect;
}) {
  return (
    <div className="pointer-events-none absolute inset-0">
      {/* Inline styles: positions are computed at runtime, no class can express them. */}
      <div
        data-testid="idp-thumbnail-boxes"
        className="absolute"
        style={{
          left: page.left,
          top: page.top,
          width: page.width,
          height: page.height,
        }}
      >
        {boxes.map(box => (
          <div
            key={box.id}
            data-testid="idp-thumbnail-box"
            data-confidence={box.level ?? 'none'}
            className={cn(
              'absolute rounded-[1px] border',
              box.level
                ? BOX_CLASS_BY_LEVEL[box.level]
                : NO_CONFIDENCE_BOX_CLASS,
            )}
            style={{
              left: `${box.left * 100}%`,
              top: `${box.top * 100}%`,
              width: `${box.width * 100}%`,
              height: `${box.height * 100}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Sends a failed thumbnail load to Sentry once, then shows the outline.
 */
function ReportFailure({
  cause,
  documentName,
  onReported,
}: {
  cause: unknown;
  documentName: string;
  onReported: () => void;
}) {
  // eslint-disable-next-line no-restricted-syntax -- reason: FileRenderer reports a failed load while rendering; reporting it and switching to the outline are side effects that have to wait until after.
  useEffect(() => {
    captureException(cause, {
      tags: {
        module: 'outputs',
        action: 'fetchIDPDocumentPreview',
        documentName: documentName || 'unknown',
      },
    });
    onReported();
  }, [cause, documentName, onReported]);
  return null;
}

/** Runs `run` once after it mounts; lets a render-time error move on. */
function OnMount({ run }: { run: () => void }) {
  // eslint-disable-next-line no-restricted-syntax -- reason: FileRenderer reports a failed load while rendering; trying the next location is a state change that has to wait until after it.
  useEffect(run, [run]);
  return null;
}

/** Props for {@link IDPDocumentThumbnail}. */
interface IIDPDocumentThumbnailProps {
  /** The source document, as a file resource name or a bare file id */
  documentName: string;
  /** The extraction's fields; those located on page 1 get a box */
  fields: IIDPField[];
  /** Height of the tile; defaults to the card preview height */
  className?: string;
}

/**
 * First page of an IDP extraction's source document with a confidence-coloured
 * box over each field. Loads through `FileRenderer` once the tile is on screen,
 * since tables draw one per row (KOG-11945).
 *
 * @param props - Component props
 * @returns A document thumbnail with field boxes
 */
export function IDPDocumentThumbnail({
  documentName,
  fields,
  className,
}: IIDPDocumentThumbnailProps) {
  const { ref, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
  });
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const candidates = useMemo(
    () => getDocumentFileUrls(documentName, organization, workspace),
    [documentName, organization, workspace],
  );
  // The attempt belongs to one set of locations; a new document starts over.
  const locationsKey = candidates.join('|');
  const [attempt, setAttempt] = useState({ key: locationsKey, index: 0 });
  const attemptIndex = attempt.key === locationsKey ? attempt.index : 0;
  const fileUrl = candidates[attemptIndex];
  const file = useMemo<TResolvedFile | null>(
    () => (fileUrl ? { type: 'file-remote', fileUrl } : null),
    [fileUrl],
  );
  const hasFallback = attemptIndex < candidates.length - 1;
  const tryNextLocation = useCallback(
    () => setAttempt({ key: locationsKey, index: attemptIndex + 1 }),
    [attemptIndex, locationsKey],
  );
  // Which set of locations has run out, so a new document loads afresh.
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const failed = file === null || failedKey === locationsKey;
  const markFailed = useCallback(
    () => setFailedKey(locationsKey),
    [locationsKey],
  );
  const boxes = useMemo(() => getThumbnailBoxes(fields), [fields]);
  const sheetMarkerRef = useRef<HTMLSpanElement>(null);
  const page = useRenderedPageRect(sheetMarkerRef);

  const shouldLoad = file !== null && isIntersecting;

  return (
    <div
      ref={ref}
      data-testid="idp-document-thumbnail"
      className={cn(
        'relative w-full overflow-hidden rounded-lg',
        className ?? 'h-24',
      )}
    >
      <DocumentPageSheet
        overlay={
          page && boxes.length > 0 ? (
            <FieldBoxes boxes={boxes} page={page} />
          ) : null
        }
      >
        <span ref={sheetMarkerRef} hidden />
        {/* Skeleton while loading, the plain outline once there's nothing to wait for. */}
        {!page &&
          (failed ? (
            <FileDocumentPreview className="absolute inset-0 h-full rounded-none border-0" />
          ) : (
            <IDPDocumentSkeleton dense className="absolute inset-0" />
          ))}
        {shouldLoad && file && (
          <MiniaturePage>
            <Suspense fallback={null}>
              <FileRenderer
                key={fileUrl}
                value={file}
                mode={ERenderMode.PREVIEW}
                // The outline is the error state, and a first 404 is expected.
                notifyOnError={false}
                // The skeleton under it is the loading state.
                showLoadingIndicator={false}
                // Only a 404 tries the next location.
                renderError={cause =>
                  hasFallback && checkIsNotFoundError(cause) ? (
                    <OnMount run={tryNextLocation} />
                  ) : failed ? null : (
                    <ReportFailure
                      cause={cause}
                      documentName={documentName}
                      onReported={markFailed}
                    />
                  )
                }
              />
            </Suspense>
          </MiniaturePage>
        )}
      </DocumentPageSheet>
    </div>
  );
}

/**
 * {@link IDPDocumentThumbnail} for any IDP output value: a single extraction,
 * or the first document of a list or bundle.
 *
 * @param props - The IDP value and the tile height
 * @returns The thumbnail, or null when the value names no document
 */
export function IDPValueThumbnail({
  value,
  className,
}: {
  value: TResolvedValue;
  className?: string;
}) {
  const first = Array.isArray(value) ? value[0] : value;
  const documentName = isIDPOutput(value)
    ? getIDPDocumentName(value)
    : first !== undefined
      ? getIDPBundleDocumentName(first)
      : undefined;
  const fields = useMemo(
    () =>
      isIDPOutput(value)
        ? getIDPFieldsFromOutput(value)
        : first !== undefined
          ? getIDPItemFields(first)
          : [],
    [first, value],
  );
  if (!documentName) return null;
  return (
    <IDPDocumentThumbnail
      documentName={documentName}
      fields={fields}
      className={className}
    />
  );
}
